import type {
  Question,
  SelectOption,
  SelectOptionDefault,
  SelectOptionGroup,
} from '../config/types';
import { isQuestionVisible } from './config-utils';
import { hasValue } from './form-utils';
import { getSelectOptionLabel, getSelectOptionValue } from './select-assets';

export type ListQuestionKind = 'text' | 'number' | 'list' | 'multi-list' | 'unsupported';

export type ListQuestionOption = {
  label: string;
  value: string;
};

export type ListQuestionCondition = {
  questionName: string;
  questionTitle: string;
  optionLabel: string;
  acceptedValues: string[];
};

export type ListQuestionDescriptor = {
  question: Question;
  kind: ListQuestionKind;
  options: ListQuestionOption[];
  conditions: ListQuestionCondition[];
};

export type ListAnswerEntry = {
  id: string;
  answers: Record<string, unknown>;
};

const DISPLAY_ONLY_QUESTION_TYPES = new Set<Question['questionType']>([
  'content',
  'hidden',
  'inset',
]);

const getQuestionKind = (question: Question): ListQuestionKind => {
  switch (question.questionType) {
    case 'radio':
    case 'select':
    case 'autoComplete':
      return 'list';
    case 'checkbox':
      return 'multi-list';
    case 'instanceInput':
    case 'costInput':
      return 'number';
    case 'textInput':
    case 'textInputSmall':
    case 'textarea':
      return question.rules?.number || question.rules?.float || question.rules?.decimal
        ? 'number'
        : 'text';
    default:
      return 'unsupported';
  }
};

const toOption = (option: string | SelectOptionDefault): ListQuestionOption =>
  typeof option === 'string'
    ? { label: option, value: option }
    : { label: getSelectOptionLabel(option), value: getSelectOptionValue(option) };

const getStaticOptions = (question: Question): ListQuestionOption[] => {
  if (!Array.isArray(question.options)) return [];

  return question.options.flatMap((option: SelectOption) => {
    if (typeof option === 'string') return [toOption(option)];
    if ('label' in option && 'options' in option) {
      return (option as SelectOptionGroup).options.map((item) => toOption(item));
    }
    return [toOption(option as SelectOptionDefault)];
  });
};

const getConditionalOptions = (question: Question): SelectOptionDefault[] =>
  Array.isArray(question.options)
    ? question.options.filter(
        (option): option is SelectOptionDefault =>
          typeof option !== 'string' &&
          'questions' in option &&
          Array.isArray((option as SelectOptionDefault).questions),
      )
    : [];

export const getListQuestionDescriptors = (
  questions: Question[],
  conditions: ListQuestionCondition[] = [],
): ListQuestionDescriptor[] => {
  const descriptors: ListQuestionDescriptor[] = [];

  questions.forEach((question) => {
    if (!DISPLAY_ONLY_QUESTION_TYPES.has(question.questionType)) {
      descriptors.push({
        question,
        kind: getQuestionKind(question),
        options: getStaticOptions(question),
        conditions,
      });
    }

    getConditionalOptions(question).forEach((option) => {
      const optionLabel = getSelectOptionLabel(option);
      const optionValue = getSelectOptionValue(option);
      descriptors.push(
        ...getListQuestionDescriptors(option.questions || [], [
          ...conditions,
          {
            questionName: question.name,
            questionTitle: question.title,
            optionLabel,
            acceptedValues: [...new Set([optionLabel, optionValue])],
          },
        ]),
      );
    });
  });

  return descriptors;
};

const conditionMatches = (answer: unknown, acceptedValues: string[]) => {
  const normalizedValues = acceptedValues.map((value) => value.trim());
  if (Array.isArray(answer)) {
    return answer.some((value) => normalizedValues.includes(String(value).trim()));
  }
  return normalizedValues.includes(String(answer ?? '').trim());
};

export const isListQuestionActive = (
  descriptor: ListQuestionDescriptor,
  rowAnswers: Record<string, unknown>,
) =>
  descriptor.conditions.every((condition) =>
    conditionMatches(rowAnswers[condition.questionName], condition.acceptedValues),
  ) && isQuestionVisible(descriptor.question, rowAnswers);

const normaliseOptionValue = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

export const getListQuestionOptionExclusionMessage = (
  descriptor: ListQuestionDescriptor,
  optionValue: unknown,
  rowAnswers: Record<string, unknown>,
): string | undefined => {
  const option = descriptor.options.find(
    ({ label, value }) =>
      normaliseOptionValue(label) === normaliseOptionValue(optionValue) ||
      normaliseOptionValue(value) === normaliseOptionValue(optionValue),
  );
  if (!option) return undefined;

  return descriptor.question.excludeOptionsFrom?.find(({ field }) => {
    const sourceValue = rowAnswers[field];
    const selectedValues: unknown[] = Array.isArray(sourceValue) ? sourceValue : [sourceValue];
    return selectedValues.some(
      (selectedValue) =>
        normaliseOptionValue(selectedValue) === normaliseOptionValue(option.label) ||
        normaliseOptionValue(selectedValue) === normaliseOptionValue(option.value),
    );
  })?.message;
};

export const getListQuestionAvailableOptions = (
  descriptor: ListQuestionDescriptor,
  rowAnswers: Record<string, unknown>,
) =>
  descriptor.options.filter(
    ({ value }) => !getListQuestionOptionExclusionMessage(descriptor, value, rowAnswers),
  );

const getMatchingDescriptor = (
  answerName: string,
  descriptors: ListQuestionDescriptor[],
): ListQuestionDescriptor | undefined =>
  descriptors
    .slice()
    .sort((left, right) => String(right.question.name).length - String(left.question.name).length)
    .find(({ question }) => {
      const name = String(question.name);
      return answerName === name || answerName.endsWith(`-${name}`);
    });

const getAnswerId = (answerName: string, questionName: string) =>
  answerName === questionName ? '1' : answerName.slice(0, -(questionName.length + 1)).trim() || '1';

export const getListAnswerEntries = (
  formAnswers: Record<string, unknown>,
  questions: Question[],
): ListAnswerEntry[] => {
  const descriptors = getListQuestionDescriptors(questions);
  const entries = new Map<string, Record<string, unknown>>();

  Object.entries(formAnswers).forEach(([answerName, answer]) => {
    const descriptor = getMatchingDescriptor(answerName, descriptors);
    if (!descriptor) return;
    const questionName = String(descriptor.question.name);
    const id = getAnswerId(answerName, questionName);
    const rowAnswers = entries.get(id) || {};
    rowAnswers[questionName] = answer;
    entries.set(id, rowAnswers);
  });

  return [...entries.entries()]
    .map(([id, answers]) => ({ id, answers }))
    .filter(({ answers }) => Object.values(answers).some(hasValue))
    .sort((left, right) => {
      const leftNumber = Number(left.id);
      const rightNumber = Number(right.id);
      if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        return leftNumber - rightNumber;
      }
      return left.id.localeCompare(right.id);
    });
};

export const getListAnswerKeys = (formAnswers: Record<string, unknown>, questions: Question[]) => {
  const descriptors = getListQuestionDescriptors(questions);
  return Object.keys(formAnswers).filter((answerName) =>
    Boolean(getMatchingDescriptor(answerName, descriptors)),
  );
};
